import React, { useState } from 'react';
import { Routes, Route, Link, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
    AppBar, Box, CssBaseline, IconButton, Container,
    Toolbar, Typography, Avatar, Menu, MenuItem, Tooltip, Button, Divider
} from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import EventIcon from '@mui/icons-material/Event';
import GavelIcon from '@mui/icons-material/Gavel';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SportsKabaddiIcon from '@mui/icons-material/SportsKabaddi';
import FunctionsIcon from '@mui/icons-material/Functions';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CalculateIcon from '@mui/icons-material/Calculate';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';

// Importeer de pagina's
import MyTeam from '../pages/participant/MyTeam';
import Rankings from '../pages/participant/Rankings';
import Schedule from '../pages/participant/Schedule';
import TransferMarket from '../pages/participant/TransferMarket';
import PlayerData from '../pages/participant/PlayerData';
import AdminAuction from '../pages/admin/AdminAuction';
import AdminGame from '../pages/admin/AdminGame';
import AdminVeilinglijst from '../pages/admin/AdminVeilinglijst';
import AdminOverview from '../pages/admin/AdminOverview';
import LegacyAdminRedirect from './LegacyAdminRedirect';
import Rules from '../pages/Rules';
import Scoring from '../pages/Scoring';
import { useAuction } from '../context/AuctionContext';
import BottomNavBar from './BottomNavBar'; // Importeer de nieuwe component

function AppLayout() {
    const { user, logout, isGlobalAdmin } = useAuth();
    const { auction } = useAuction();
    const navigate = useNavigate();
    const location = useLocation();
    const [anchorEl, setAnchorEl] = useState(null);

    const handleMenu = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const handleLogout = () => {
        handleClose();
        logout();
    };

    // Twee losse rollen. isGlobalAdmin beheert het hele spel (seizoenen, stamgegevens,
    // puntentelling) via /admin; isSubleagueAdmin beheert alleen deze veiling.
    const isSubleagueAdmin = Boolean(user && auction && user.uid === auction.adminId);
    const canManageSubleague = isGlobalAdmin || isSubleagueAdmin;
    const isAdminView = canManageSubleague && location.pathname.includes(`/auction/${auction.id}/beheer`);

    const adminNav = auction ? [
        { text: 'Overzicht', icon: <GavelIcon />, path: `/auction/${auction.id}/beheer/overzicht` },
        { text: 'Veilingavond', icon: <GavelIcon />, path: `/auction/${auction.id}/beheer/veilingavond` },
        { text: 'Spelverloop', icon: <SportsEsportsIcon />, path: `/auction/${auction.id}/beheer/spelverloop` },
        { text: 'Veilinglijst', icon: <PeopleAltIcon />, path: `/auction/${auction.id}/beheer/veilinglijst` },
    ] : [];

    const participantNav = auction ? [
        { text: 'Team', icon: <GroupIcon />, path: `/auction/${auction.id}/my-team` },
        { text: 'Stand', icon: <LeaderboardIcon />, path: `/auction/${auction.id}/rankings` },
        { text: 'Wedstrijden', icon: <EventIcon />, path: `/auction/${auction.id}/schedule` },
        { text: 'Spelersdata', icon: <FunctionsIcon />, path: `/auction/${auction.id}/player-data` },
        { text: 'Transfers', icon: <SwapHorizIcon />, path: `/auction/${auction.id}/transfer-market` },
    ] : [];

    const navItems = isAdminView ? adminNav : participantNav;

    const handleSwitchView = () => {
        handleClose();
        if (isAdminView) {
            navigate(`/auction/${auction.id}/my-team`);
        } else {
            navigate(`/auction/${auction.id}/beheer/overzicht`);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <CssBaseline />
            <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                <Toolbar>
                    <Box component={Link} to={`/auction/${auction.id}`} sx={{ display: 'flex', alignItems: 'center', color: 'inherit', textDecoration: 'none' }}>
                        <img src="/logo192.png" alt="Spelveilingen logo" style={{ height: '60px', marginRight: '5px' }} />
                        <Typography
                            variant="h6"
                            component="div"
                        >
                            Spelveilingen
                        </Typography>
                    </Box>
                    <Box sx={{ flexGrow: 1 }} />

                    <Box sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center', position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
                        {navItems.map((item) => {
                            const isActive = location.pathname.startsWith(item.path);
                            return (
                                <Button
                                    key={item.text}
                                    component={Link}
                                    to={item.path}
                                    sx={{
                                        my: 2,
                                        color: 'white',
                                        display: 'block',
                                        fontWeight: isActive ? 700 : 400,
                                        borderBottom: isActive ? '3px solid' : '3px solid transparent',
                                        borderColor: isActive ? 'primary.main' : 'transparent',
                                        borderRadius: 0,
                                        px: 2,
                                    }}
                                >
                                    {item.text}
                                </Button>
                            );
                        })}
                    </Box>
                    <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }} />

                    <Box>
                        <Tooltip title="Account instellingen">
                            <IconButton onClick={handleMenu} sx={{ p: 0 }}>
                                <Avatar>{(user?.name || user?.email || '').charAt(0).toUpperCase()}</Avatar>
                            </IconButton>
                        </Tooltip>
                        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
                            <MenuItem onClick={() => { navigate('/my-auctions'); handleClose(); }}>
                                <GavelIcon sx={{ mr: 1 }} />
                                Mijn Veilingen
                            </MenuItem>
                            <MenuItem onClick={() => { navigate('/ranglijst-aller-tijden'); handleClose(); }}>
                                <LeaderboardIcon sx={{ mr: 1 }} />
                                Ranglijst aller tijden
                            </MenuItem>
                            <MenuItem onClick={() => { navigate('/prijzenkast'); handleClose(); }}>
                                <EmojiEventsIcon sx={{ mr: 1 }} />
                                Prijzenkast
                            </MenuItem>
                            <MenuItem onClick={() => { navigate(`/auction/${auction.id}/rules`); handleClose(); }} disabled={!auction}>
                                <MenuBookIcon sx={{ mr: 1 }} />
                                Spelregels
                            </MenuItem>
                            <MenuItem onClick={() => { navigate(`/auction/${auction.id}/scoring`); handleClose(); }} disabled={!auction}>
                                <CalculateIcon sx={{ mr: 1 }} />
                                Puntentelling
                            </MenuItem>
                            <MenuItem onClick={() => { navigate('/account'); handleClose(); }}>
                                <AccountCircleIcon sx={{ mr: 1 }} />
                                Account
                            </MenuItem>
                            {isGlobalAdmin && <Divider />}
                            {isGlobalAdmin && (
                                <MenuItem onClick={() => { navigate('/admin'); handleClose(); }}>
                                    <AdminPanelSettingsIcon sx={{ mr: 1 }} />
                                    Administrator
                                </MenuItem>
                            )}
                            {canManageSubleague && <Divider />}
                            {canManageSubleague && (
                                <MenuItem onClick={handleSwitchView}>
                                    {isAdminView ? <SportsKabaddiIcon sx={{ mr: 1 }} /> : <AdminPanelSettingsIcon sx={{ mr: 1 }} />}
                                    {isAdminView ? 'Wissel naar Deelnemer' : 'Wissel naar Admin'}
                                </MenuItem>
                            )}
                            <Divider />
                            <MenuItem onClick={handleLogout}>
                                <LogoutIcon sx={{ mr: 1 }} />
                                Uitloggen
                            </MenuItem>
                        </Menu>
                    </Box>
                </Toolbar>
            </AppBar>
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    width: '100%',
                    pb: { xs: 7, md: 0 },
                    // In standalone groeit de bottom bar met de safe-area mee; geef de
                    // content dezelfde extra ruimte zodat er niets achter verdwijnt.
                    '@media (display-mode: standalone)': {
                        pb: { xs: 'calc(56px + env(safe-area-inset-bottom))', md: 0 },
                    },
                }}
            >
                <Toolbar />
                <Container maxWidth="lg" sx={{ pt: 3, pb: 3 }}>
                    <Routes>
                        {/* Redirect from auction root to the correct default page */}
                        <Route index element={<Navigate to={canManageSubleague ? 'beheer/overzicht' : 'my-team'} replace />} />

                        {/* Participant Routes */}
                        <Route path="my-team" element={<MyTeam />} />
                        <Route path="rankings" element={<Rankings />} />
                        <Route path="schedule" element={<Schedule />} />
                        <Route path="player-data" element={<PlayerData />} />
                        <Route path="transfer-market" element={<TransferMarket />} />

                        {/* Subleaguebeheer */}
                        {canManageSubleague && <Route path="beheer/overzicht" element={<AdminOverview />} />}
                        {canManageSubleague && <Route path="beheer/veilingavond" element={<AdminAuction />} />}
                        {canManageSubleague && <Route path="beheer/spelverloop" element={<AdminGame />} />}
                        {canManageSubleague && <Route path="beheer/veilinglijst" element={<AdminVeilinglijst />} />}

                        {/* Oude /admin/*-paden blijven werken zodat bookmarks niet breken. */}
                        <Route path="admin/*" element={<LegacyAdminRedirect auction={auction} />} />

                        {/* Common Routes */}
                        <Route path="rules" element={<Rules />} />
                        <Route path="scoring" element={<Scoring />} />

                        {/* Fallback for any other route within the auction */}
                        <Route path="*" element={<Navigate to={canManageSubleague ? 'beheer/overzicht' : 'my-team'} replace />} />
                    </Routes>
                </Container>
            </Box>
            <BottomNavBar navItems={navItems} />
        </Box>
    );
}

export default AppLayout;
